// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-COMMAND — fail-closed edit veto (PURE, no store reads, no I/O)
// ═══════════════════════════════════════════════════════════════════════════
//
// The ACTUAL fail-closed enforcement mechanism this PR ships: given the set
// of entities a dispatch is about to mutate, veto the WHOLE dispatch when any
// touched entity — or the layer it lives on — is currently soft-locked by
// ANOTHER collaborator. `editorStore.ts`'s `dispatch()` calls this
// synchronously, immediately after computing the dispatch's `EntityMutation`
// diff and BEFORE any inverse-computation / history-recording / commit —
// so a veto here is a true no-op: the ORIGINAL pre-dispatch state is
// returned untouched, atomically (see the call site for why that is
// guaranteed).
//
// PURE by construction: no store reads (`locks` / `selfParticipantId` are
// passed in — the caller reads `useCollabLockStore.getState()` itself), no
// I/O, no editorStore import. This is what makes it exhaustively unit-
// testable AND what guarantees the ZERO-OVERHEAD-WHEN-DISCONNECTED
// invariant: an empty `locks` map (the default, disconnected state) can
// never match anything, so a normal (non-collab) user's every-day edit is
// NEVER slowed or altered by this function's logic — it is a single empty-
// map lookup that always returns `null`.
//
// Source of the entity/layer scope precedence + "veto the whole batch"
// semantics: the PR spec (COLLAB-LOCK-COMMAND) — AutoCAD parity for a locked
// layer ("you cannot edit an object on a locked layer") extended to the
// per-collaborator soft-lock case; a partial apply of a multi-entity command
// would leave the document in a state no single command produced, which is
// itself an AutoCAD-parity violation (every command is all-or-nothing).
//
// ── LAYER-TABLE-ONLY touches (`touchedLayerNames`) ──────────────────────────
// `header/update-layer` / `header/delete-layer` mutate the layer RECORD
// itself (rename / recolor / delete the definition) — `dispatchMutations`
// carries this as a `styleTablesTouched` entry, NOT an entity add/remove/
// update, so no `CollabLockVetoTouchedEntity` is ever produced for it. A
// dispatch that touches a layer's own record this way is checked against
// `touchedLayerNames` instead — LAYER-scope only (there is no entity
// involved, so no entity-scope check makes sense here).
//
// ── Case-insensitive LAYER-scope matching ───────────────────────────────────
// `LOCK LAYER <name>` resolves the name via `drawingObjectGraph.getLayer`
// (case-insensitive) and stores the CANONICAL `layer.name` as the lock's
// `targetId`, but an entity's raw `.layer` string (or a layer-table command's
// `layerName`) can carry different casing (imported drawings are not
// guaranteed consistent). LAYER-scope comparisons here are therefore
// case-insensitive; ENTITY-scope (DXF handle) comparisons stay EXACT —
// handles are opaque tokens, not user-authored names, so case-folding them
// would be a semantic change, not a compatibility fix.
// ═══════════════════════════════════════════════════════════════════════════

import { lockScopeKey, type LockScope, type LockState } from "../collab/collabLockTypes";

/** One entity a dispatch is about to touch — the minimal projection the veto needs. */
export type CollabLockVetoTouchedEntity = {
  entityId: string;
  /** The entity's layer, or `undefined` when unknown (only the entity-scope lock is then checked). */
  layerName: string | undefined;
};

export type CollabLockVetoInput = {
  /** Every entity the pending dispatch would add/remove/update, in a DETERMINISTIC order. */
  touchedEntities: ReadonlyArray<CollabLockVetoTouchedEntity>;
  /**
   * Layer names a dispatch touches DIRECTLY as a layer-table record edit
   * (`header/update-layer` / `header/delete-layer`) — no entity is involved,
   * so only the LAYER-scope lock is checked for each name. Checked AFTER
   * every `touchedEntities` entry, in the given order.
   */
  touchedLayerNames: readonly string[];
  /** The live soft-lock map (`lockScopeKey` → `LockState`). Empty while disconnected. */
  locks: ReadonlyMap<string, LockState>;
  /** This tab's own server-minted participant id, or `null` before pairing. */
  selfParticipantId: string | null;
  /**
   * P1 (post-review, round 5) — whether `locks` is a CURRENT, trustworthy
   * picture of the room's soft-lock state. `true` for a genuinely
   * disconnected/no-session tab (there is trivially nothing to miss) AND for
   * a live `"connected"` session (the map is kept in sync by `welcome` +
   * `lock-update`). `false` for a tab that WAS connected and is now
   * `"connecting"` (mid-reconnect) or `"error"` — `CollabLockClient.
   * resetTrackedState()` clears `locks` to EMPTY on those transitions even
   * though the true server-side picture may be non-empty and is simply
   * UNKNOWN right now. An empty-but-non-authoritative `locks` map must NOT
   * be read as "nothing is locked" (that would defeat fail-closed) — see
   * the `locks.size === 0` fast path below, which now also requires
   * `authoritative`.
   */
  authoritative: boolean;
};

/**
 * `null` = no veto (the dispatch may proceed).
 * `{ kind: "locked-by-other" }` = the FIRST blocking lock found + its
 * holder's display name (the ORIGINAL, scope-specific veto shape).
 * `{ kind: "unavailable" }` = the caller cannot currently prove the touched
 * scopes are NOT locked (a reconnecting/errored session whose last-known
 * `locks` snapshot was cleared, not confirmed empty) — fail-closed: treated
 * as a veto with no specific scope/owner to report.
 */
export type CollabLockVetoResult =
  | null
  | { kind: "locked-by-other"; scope: LockScope; heldByName: string }
  | { kind: "unavailable" };

/**
 * Evaluate whether a pending dispatch touches any entity/layer currently
 * soft-locked by ANOTHER collaborator. Iterates `touchedEntities` in the
 * given order, checking the ENTITY-scope lock before the LAYER-scope lock
 * for each entry (deterministic — the first violation found wins), then
 * `touchedLayerNames` (layer-table-only edits) in order, and returns the
 * single first violation found. A lock held by `selfParticipantId` itself is
 * NOT a veto (AutoCAD parity: you may always continue editing what you hold
 * the lock on). Returns `null` when `locks` is empty AND `authoritative`
 * (the common, disconnected-or-uncontended case) or when nothing touched is
 * locked by anyone else. Returns `{ kind: "unavailable" }` when `locks` is
 * empty but NOT `authoritative` (P1, post-review round 5) AND the pending
 * dispatch actually touches at least one entity/layer scope (P2, post-review
 * round 6) — fail-closed rather than silently allowing an edit whose true
 * lock status is unknown. A dispatch that touches NOTHING (`touchedEntities`
 * and `touchedLayerNames` both empty — the shape `dispatch()` builds for a
 * `NO_MUTATIONS` command, e.g. opening PROPERTIES or changing selection) has
 * no scope for the unknown snapshot to possibly conflict with, so it is
 * never vetoed regardless of `authoritative` — otherwise a reconnecting/
 * errored collab session would reject every read-only/UI command as "Edit
 * rejected" even though it never touches a lock-relevant scope.
 */
export function evaluateCollabLockVeto(input: CollabLockVetoInput): CollabLockVetoResult {
  const touchesAnyScope = input.touchedEntities.length > 0 || input.touchedLayerNames.length > 0;
  if (input.locks.size === 0) {
    if (!input.authoritative) {
      if (!touchesAnyScope) {
        return null; // nothing touched — an unknown snapshot has nothing to conflict with.
      }
      // The caller's last-known snapshot was cleared (reconnecting/errored)
      // — `locks` being empty here proves nothing about the true
      // server-side state. Fail-closed: block rather than guess "allowed".
      return { kind: "unavailable" };
    }
    return null; // zero-overhead fast path — no collab session, or no locks held anywhere.
  }

  // A case-insensitive layer-scope index, built AT MOST once per call and
  // ONLY when a layer-scope check can actually happen — shared by BOTH the
  // entity-derived `touched.layerName` check and the direct
  // `touchedLayerNames` check below (one code path, not two). Left `null`
  // when there is nothing to check (no touched layer name at all) or when
  // `locks` holds zero layer-scope entries, so a pure entity-scope dispatch
  // (the overwhelmingly common case) pays no extra iteration cost.
  let layerLockIndex: Map<string, { scope: LockScope; lock: LockState }> | null = null;
  const mayNeedLayerCheck =
    input.touchedLayerNames.length > 0 ||
    input.touchedEntities.some((touched) => touched.layerName !== undefined);
  if (mayNeedLayerCheck) {
    for (const lock of input.locks.values()) {
      if (lock.scope.kind !== "layer") continue;
      layerLockIndex ??= new Map();
      layerLockIndex.set(lock.scope.targetId.toLowerCase(), { scope: lock.scope, lock });
    }
  }
  const checkLayerName = (layerName: string): CollabLockVetoResult => {
    if (layerLockIndex === null) return null;
    const entry = layerLockIndex.get(layerName.toLowerCase());
    if (entry !== undefined && entry.lock.ownerId !== input.selfParticipantId) {
      return { kind: "locked-by-other", scope: entry.scope, heldByName: entry.lock.ownerName };
    }
    return null;
  };

  for (const touched of input.touchedEntities) {
    const entityScope: LockScope = { kind: "entity", targetId: touched.entityId };
    const entityLock = input.locks.get(lockScopeKey(entityScope));
    if (entityLock !== undefined && entityLock.ownerId !== input.selfParticipantId) {
      return { kind: "locked-by-other", scope: entityScope, heldByName: entityLock.ownerName };
    }
    if (touched.layerName !== undefined) {
      const layerVeto = checkLayerName(touched.layerName);
      if (layerVeto) return layerVeto;
    }
  }
  for (const layerName of input.touchedLayerNames) {
    const layerVeto = checkLayerName(layerName);
    if (layerVeto) return layerVeto;
  }
  return null;
}
